/**
 * ParlayContext - Shared state for parlay builder
 *
 * Allows Knewton chat, streaks page, and parlay workshop
 * to share the same parlay items.
 */

import { createContext, useContext, useState, useCallback } from 'react';

const ParlayContext = createContext(null);

/**
 * Create a unique key for a player pick.
 * Uses player_name + stat_type + threshold since player_id may be null/0 for some sources.
 */
const getPlayerKey = (player) => {
  return `${player.player_name || ''}_${player.stat_type || ''}_${player.threshold || ''}`;
};

export function ParlayProvider({ children }) {
  const [parlayItems, setParlayItems] = useState([]);

  const addToParlay = useCallback((player) => {
    console.log('Adding to parlay:', player);
    setParlayItems(prev => {
      const playerKey = getPlayerKey(player);
      // Check if already in parlay by unique key
      const exists = prev.some(p => getPlayerKey(p) === playerKey);
      if (exists) {
        console.log('Player already in parlay:', playerKey);
        return prev;
      }
      console.log('Added player to parlay:', playerKey);
      return [...prev, player];
    });
  }, []);

  const removeFromParlay = useCallback((player) => {
    const playerKey = getPlayerKey(player);
    console.log('Removing from parlay:', playerKey);
    setParlayItems(prev => prev.filter(p => getPlayerKey(p) !== playerKey));
  }, []);

  const isInParlay = useCallback((player) => {
    const playerKey = getPlayerKey(player);
    return parlayItems.some(p => getPlayerKey(p) === playerKey);
  }, [parlayItems]);

  const clearParlay = useCallback(() => {
    setParlayItems([]);
  }, []);

  const value = {
    parlayItems,
    addToParlay,
    removeFromParlay,
    isInParlay,
    clearParlay,
    parlayCount: parlayItems.length
  };

  return (
    <ParlayContext.Provider value={value}>
      {children}
    </ParlayContext.Provider>
  );
}

export function useParlay() {
  const context = useContext(ParlayContext);
  if (!context) {
    throw new Error('useParlay must be used within a ParlayProvider');
  }
  return context;
}
